import { useState } from 'react';
import { erroDosPassageiros } from './validacao';
import { useGruposPassageiros } from '../../grupos/useGruposPassageiros';
import { GrupoPassageirosDialog } from '../../grupos/GrupoPassageirosDialog';
import { X } from 'lucide-react';
import type { Passageiro } from './types';

interface Props {
  tomaNome: string;
  tomaCpfCnpj: string;
  passageiros: Passageiro[];
  capacidade?: number | null;
  onTomador: (nome: string, cpfCnpj: string) => void;
  onPassageiros: (p: Passageiro[]) => void;
  /** O grupo aplicado, para o wizard anotar na ATF depois de emitir. */
  onGrupoAplicado?: (idGrupo: number | null) => void;
}

/**
 * Passo 6 — handoff §2: tabela com header, campo vazio obrigatório com
 * borda de alerta, capacidade do veículo. Os três campos por passageiro são
 * obrigatórios em TODOS, exigência do portal do DER.
 */
export function PassoPassageiros({
  tomaNome, tomaCpfCnpj, passageiros, capacidade, onTomador, onPassageiros, onGrupoAplicado,
}: Props) {
  const { grupos } = useGruposPassageiros();
  const [dialogSalvar, setDialogSalvar] = useState(false);
  const erro = erroDosPassageiros(passageiros);
  const acimaCapacidade = capacidade != null && passageiros.length > capacidade;

  /**
   * Mexer na lista desfaz o vínculo com o grupo aplicado.
   *
   * Sem isso, a ATF era anotada com o nome de um grupo que não corresponde a
   * quem realmente viajou — e a anotação existe justamente para dizer com que
   * lista a emissão foi feita.
   */
  function editou(novos: Passageiro[]) {
    onPassageiros(novos);
    onGrupoAplicado?.(null);
  }

  function atualizar(i: number, campo: keyof Passageiro, valor: string) {
    editou(passageiros.map((p, idx) => idx === i ? { ...p, [campo]: valor } : p));
  }

  function remover(i: number) {
    editou(passageiros.filter((_, idx) => idx !== i));
  }

  function adicionar() {
    editou([...passageiros, { nome: '', docIdentificacao: '', orgaoExpedidor: '' }]);
  }

  return (
    <div className="co-pilha">
      <div className="co-grade-2">
        <label className="co-campo">
          <span className="co-campo-rotulo">Contratante</span>
          <input className="co-entrada" value={tomaNome} onChange={e => onTomador(e.target.value, tomaCpfCnpj)} />
        </label>
        <label className="co-campo">
          <span className="co-campo-rotulo">CPF ou CNPJ</span>
          <input
            className="co-entrada co-numero" inputMode="numeric"
            value={tomaCpfCnpj} onChange={e => onTomador(tomaNome, e.target.value)}
          />
        </label>
      </div>

      <div>
        <div className="co-passageiros-cabeca">
          <span className="co-titulo-cartao co-numero">
            {passageiros.length}{capacidade != null && ` de ${capacidade}`} lugares
          </span>

          <div className="co-filtros-grupo">
            {grupos.length > 0 && (
              <select
                className="co-entrada"
                aria-label="Usar um grupo salvo"
                value=""
                onChange={e => {
                  const g = grupos.find(x => String(x.idGrupo) === e.target.value);
                  if (!g) return;
                  // Substitui a lista: aplicar um grupo é escolher QUEM viaja,
                  // não acrescentar a quem já estava.
                  onPassageiros(g.passageiros.map(p => ({ ...p })));
                  onGrupoAplicado?.(g.idGrupo);
                }}
              >
                <option value="">Usar um grupo salvo…</option>
                {grupos.map(g => (
                  <option key={g.idGrupo} value={g.idGrupo}>
                    {g.nome} ({g.passageiros.length})
                  </option>
                ))}
              </select>
            )}
            {passageiros.some(p => p.nome.trim()) && (
              <button type="button" className="co-btn co-btn-texto" onClick={() => setDialogSalvar(true)}>
                Salvar como grupo
              </button>
            )}
          </div>
        </div>

        {passageiros.length > 0 && (
          <div className="co-passageiro co-passageiros-rotulos" aria-hidden="true">
            <span />
            <span>Nome</span>
            <span>Documento</span>
            <span>Órgão</span>
            <span />
          </div>
        )}
        {passageiros.map((p, i) => (
          <div key={i} className="co-passageiro">
            <span className="co-passageiro-numero">{i + 1}</span>
            <input
              className="co-entrada" placeholder="Nome" aria-label={`Nome do passageiro ${i + 1}`}
              aria-invalid={!p.nome || undefined}
              value={p.nome} onChange={e => atualizar(i, 'nome', e.target.value)}
            />
            <input
              className="co-entrada" placeholder="Documento" aria-label={`Documento do passageiro ${i + 1}`}
              aria-invalid={!p.docIdentificacao || undefined}
              value={p.docIdentificacao} onChange={e => atualizar(i, 'docIdentificacao', e.target.value)}
            />
            <input
              className="co-entrada" placeholder="Órgão" aria-label={`Órgão expedidor do passageiro ${i + 1}`}
              aria-invalid={!p.orgaoExpedidor || undefined}
              value={p.orgaoExpedidor} onChange={e => atualizar(i, 'orgaoExpedidor', e.target.value)}
            />
            <button type="button" className="co-btn-icone" aria-label={`Remover o passageiro ${i + 1}`} onClick={() => remover(i)}>
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        ))}

        <button type="button" className="co-btn co-btn-texto" onClick={adicionar}>
          Adicionar passageiro
        </button>
      </div>

      {acimaCapacidade && <p className="co-nota co-nota-atencao" role="alert">Acima da capacidade do veículo.</p>}
      {erro && <p className="co-apoio" role="status">{erro}</p>}

      <GrupoPassageirosDialog
        aberto={dialogSalvar}
        passageiros={passageiros}
        onFechar={() => setDialogSalvar(false)}
        onSalvo={idGrupo => onGrupoAplicado?.(idGrupo)}
      />
    </div>
  );
}
